// Page - Dashboard Overview (NexGEO Version)
import { useState, useEffect } from 'react';
import { Activity, Target, Search, Box, Key, Cpu, Sparkles } from 'lucide-react';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../config';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

// eslint-disable-next-line no-unused-vars
const StatCard = ({ title, value, icon: IconComponent, color, subtitle, children }) => (
  <div className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm p-5 flex flex-col border border-gray-100 dark:border-slate-700/50 hover:shadow-md transition h-full">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-500 dark:text-slate-400 text-xs font-medium">{title}</p>
        <div className="flex items-baseline gap-1 mt-2">
          <h3 className="text-4xl font-extrabold text-gray-900 dark:text-slate-100 font-mono tracking-tight">{value}</h3>
          {subtitle && <span className="text-sm font-medium text-gray-400 ml-1">{subtitle}</span>}
        </div>
      </div>
      <div className={`p-2 rounded-xl ${color} bg-opacity-10 dark:bg-opacity-20 flex-shrink-0`}>
        <IconComponent className={`w-5 h-5 ${color.replace('bg-', 'text-').replace('-500', '-600').replace('dark:', '')}`} />
      </div>
    </div>
    {/* Optional Children area for extra content like Engine Logos */}
    {children && (
      <div className="mt-4 pt-3 border-t border-gray-50 dark:border-slate-700/30 flex-1 flex items-end">
        {children}
      </div>
    )}
  </div>
);

// Helper for Mock Engine Logos
const EngineLogo = ({ name, colorCode }) => (
  <div 
    className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-800 -ml-2 first:ml-0"
    style={{ backgroundColor: colorCode }}
    title={name}
  >
    {name.charAt(0)}
  </div>
);

export default function Overview() {
  const [stats, setStats] = useState({
    totalEngines: 0,
    brandsCount: 0,
    competitorsCount: 0,
    tasksCount: 0,
    keywordsCount: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [trend, setTrend] = useState({
    labels: ['第1天', '第2天', '第3天', '第4天', '第5天', '第6天', '第7天'],
    data: [0, 0, 0, 0, 0, 0, 0],
    total: 0
  });
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);

  // Chart Data for "AI Replies Monitored"
  const trendData = {
    labels: trend.labels,
    datasets: [
      {
        label: '每日处理AI回复数',
        data: trend.data,
        borderColor: 'rgb(41, 150, 207)', // Sidebar-to brand color
        backgroundColor: 'rgba(41, 150, 207, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw} 条回复`
        }
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(200, 200, 200, 0.1)' } },
      x: { grid: { display: false } }
    }
  };

  // Welcome Banner Logic
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 6 && hour < 12) return '早上好';
    if (hour >= 12 && hour < 14) return '中午好';
    if (hour >= 14 && hour < 19) return '下午好';
    if (hour >= 19 && hour < 24) return '晚上好';
    return '夜深了';
  };

  let userName = '主理人';
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userObj = JSON.parse(userStr);
      userName = userObj.name || userObj.accountName || userObj.username || '主理人';
    }
  } catch (e) {}

  const greeting = getGreeting();
  const timeString = currentTime.toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit' });
  const dateString = currentTime.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const bid = localStorage.getItem('globalBrand');
        
        const res = await axios.get(`${API_URL}/overview/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fetchMode: localStorage.getItem('globalFetchMode') || 'hybrid',
            brandProjectId: (bid === 'all' || bid === 'undefined' || !bid) ? undefined : bid
          }
        });
        
        if (res.data.success && res.data.data) {
          // Merge actual stats with our mock display format
          setStats(prev => ({
             ...prev,
             brandsCount: res.data.data.stats.brandsCount ?? prev.brandsCount,
             competitorsCount: res.data.data.stats.competitorsCount ?? prev.competitorsCount,
             keywordsCount: res.data.data.stats.trackedKeywords ?? prev.keywordsCount,
             totalEngines: res.data.data.stats.totalEngines ?? prev.totalEngines,
             tasksCount: res.data.data.stats.tasksCount ?? prev.tasksCount
          }));
          
          setRecentActivities(res.data.data.recentActivities);
          
          if (res.data.data.trend) {
            setTrend(res.data.data.trend);
          }
        }
      } catch (e) {
        console.error("Failed to load overview data", e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    window.addEventListener('globalFilterChange', fetchData);
    return () => window.removeEventListener('globalFilterChange', fetchData);
  }, []);

  return (
    <div className="space-y-6 max-w-full p-6 pb-20">
      {/* Welcome Banner */}
      <div className="relative mb-6 flex flex-col justify-center overflow-hidden rounded-2xl bg-white dark:bg-slate-800/50 shadow-sm border border-gray-100 dark:border-slate-700 p-6 sm:px-8 sm:py-6">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-full blur-3xl mix-blend-multiply dark:mix-blend-screen opacity-70 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-gradient-to-tr from-purple-500/20 to-pink-500/20 rounded-full blur-3xl mix-blend-multiply dark:mix-blend-screen opacity-70 animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5 text-gray-900 dark:text-gray-100">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              <h2 className="text-2xl font-bold tracking-tight">
                {greeting}，{userName}
              </h2>
            </div>
            <p className="text-base text-gray-600 dark:text-gray-400 mt-1 font-light">
              欢迎回到 <span className="font-semibold text-indigo-600 dark:text-indigo-400">Sapient Rank 思序</span>。
            </p>
            <h1 className="text-lg md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-cyan-600 dark:from-indigo-400 dark:to-cyan-400 mt-4 tracking-wide">
              "明澈一点思绪，洞悉万千秩序。"
            </h1>
          </div>
          
          <div className="text-left md:text-right">
            <div className="text-3xl font-light tracking-wider font-mono text-gray-800 dark:text-gray-200">
              {currentTime.toLocaleTimeString('zh-CN', { hour12: false })}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-widest font-mono">
              {currentTime.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-')}
            </div>
          </div>
        </div>
      </div>

      {/* 模块一：横向 4 个卡片 (KPI 概览) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="全域监控品牌数"
          value={stats.brandsCount}
          subtitle="个"
          icon={Box}
          color="bg-indigo-500"
        >
          <div className="text-xs text-gray-400 mt-2">
            建立专属品牌知识库，持续监控全域风评，沉淀品牌资产。
          </div>
        </StatCard>

        <StatCard
          title="动态追踪竞品数"
          value={stats.competitorsCount}
          subtitle="个"
          icon={Target}
          color="bg-rose-500"
        >
          <div className="text-xs text-gray-400 mt-2">
            横向对比分析，实时掌握竞争对手在AI引擎中的排位与声量。
          </div>
        </StatCard>

        <StatCard
          title="累计调度任务数"
          value={stats.tasksCount}
          subtitle="次"
          icon={Activity}
          color="bg-amber-500"
        >
          <div className="text-xs text-gray-400 mt-2">
            7×24小时自动化采集集群，保障多引擎数据的时效性与准确性。
          </div>
        </StatCard>

        <StatCard
          title="核心布控词条数"
          value={stats.keywordsCount}
          subtitle="条"
          icon={Key}
          color="bg-emerald-500"
        >
          <div className="text-xs text-gray-400 mt-2">
            涵盖品牌词、产品词、行业词及各类长尾检索场景。
          </div>
        </StatCard>
      </div>

      {/* 模块二：图表与近期调度动态 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* 左侧图表：近7天AI回复监控量趋势 */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm border border-gray-100 dark:border-slate-700/50 p-6">
           <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">
               近7天监控AI回复量趋势
            </h3>
            <div className="text-sm text-gray-500 dark:text-slate-400">
               累计贡献挖掘: <span className="font-bold text-sky-600 dark:text-sky-400">{trend.total.toLocaleString()}</span> 条
            </div>
          </div>
          <div className="h-[320px] w-full">
            <Line data={trendData} options={chartOptions} />
          </div>
        </div>

        {/* 右侧列表：近期调度动态 (含品牌信息) */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl rounded-xl shadow-sm border border-gray-100 dark:border-slate-700/50 overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/80">
            <h3 className="font-bold text-gray-900 dark:text-slate-200 flex items-center gap-2">
               <Activity className="w-4 h-4 text-sidebar-to" /> 近期调度动态
            </h3>
          </div>
          <div className="p-4 flex-1 overflow-hidden">
            {recentActivities.length === 0 ? (
              <div className="text-center text-sm text-gray-400 py-8">暂无抓取记录</div>
            ) : (
              <div className="space-y-4">
                {recentActivities.slice(0, 5).map(activity => (
                  <div key={activity.id} className="flex justify-between items-start border-b border-gray-50 dark:border-slate-700/30 pb-3 last:border-0 last:pb-0">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                        {/* Fake Brand info injection if missing, usually would come from activity.brandName */}
                        <span className="text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400 rounded">
                          {activity.brandName || '品牌'}
                        </span>
                        <span className="truncate max-w-[140px]" title={activity.keyword}>
                          {activity.keyword}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                        <Search className="w-3 h-3" /> {activity.engine} · {activity.time}
                      </div>
                    </div>
                    <div className="ml-2">
                      <span className={`px-2 py-1 rounded-md text-[10px] font-medium ${
                        activity.status === 'COMPLETED' ? 'bg-[#ebf6db] text-[#5f8b1e] dark:bg-green-500/10 dark:text-green-400' : 
                        activity.status === 'RUNNING' || activity.status === 'ANALYZED' ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400' : 
                        activity.status === 'PENDING' ? 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' :
                        activity.status === 'SCHEDULED' ? 'bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-300' :
                        'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
                      }`}>
                        {activity.status === 'COMPLETED' ? '已完成' : 
                         activity.status === 'RUNNING' ? '抓取中' :
                         activity.status === 'ANALYZED' ? '已分析' :
                         activity.status === 'PENDING' ? '排队中' : 
                         activity.status === 'SCHEDULED' ? '定时待机' :
                         activity.status === 'PAUSED' ? '已暂停' :
                         activity.status === 'STOPPED' ? '已终止' :
                         activity.status === 'FAILED' ? '异常' : activity.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
